/** SCARTLEAD.TV analytics beacon v3 — stable tunes + separate views/watch. */
const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
const VIEWER_KEY = "crt_stats_viewer_v2";
const SESSION_KEY = "crt_stats_session_v2";
const TELEMETRY_VERSION = 3;
const TUNE_STABLE_MS = 750;

let _enabled = false;
// Track the exact session that received its visit. A boolean is insufficient
// because sessionStorage identity can change while this JS lifecycle survives.
let _visitedSessionId = null;
let _lastTuneChannel = null;
let _pendingTuneChannel = null;
let _pendingTuneTimer = null;
let _lastViewKey = null;
let _signedIn = false;

function nonce(prefix) {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function getViewerId() {
  try {
    let id = localStorage.getItem(VIEWER_KEY);
    if (!id) { id = nonce("viewer"); localStorage.setItem(VIEWER_KEY, id); }
    return id;
  } catch (_) { return nonce("mem-viewer"); }
}

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) { id = nonce("session"); sessionStorage.setItem(SESSION_KEY, id); }
    return id;
  } catch (_) { return nonce("mem-session"); }
}

function clearPendingTune() {
  if (_pendingTuneTimer) clearTimeout(_pendingTuneTimer);
  _pendingTuneTimer = null;
  _pendingTuneChannel = null;
}

export function setSignedIn(v) { _signedIn = !!v; }

export function setAnalyticsEnabled(v) {
  _enabled = !!v;
  if (!_enabled) {
    clearPendingTune();
    _visitedSessionId = null;
    _lastTuneChannel = null;
    _lastViewKey = null;
  }
}

async function sendEvent(payload, sessionId) {
  if (!_enabled || !sessionId) return;
  try {
    await fetch(`${API}/stats/event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...payload,
        telemetry_version: TELEMETRY_VERSION,
        session_id: sessionId,
        browser_id: getViewerId(),
        signed_in: _signedIn,
      }),
      keepalive: true,
    });
  } catch (_) { /* telemetry never breaks playback */ }
}

async function ensureSessionVisit(sessionId) {
  if (!_enabled || !sessionId || _visitedSessionId === sessionId) return;
  _visitedSessionId = sessionId;
  await sendEvent({
    type: "visit",
    referrer: (typeof document !== "undefined" && document.referrer) || "",
  }, sessionId);
}

async function post(payload) {
  if (!_enabled) return;
  const sessionId = getSessionId();
  if (!sessionId) return;

  // Phase 2.3.69-r1.3.4 — every non-visit telemetry event self-heals session
  // entry ownership. If sessionStorage changes while this JS lifecycle survives,
  // the new session receives exactly one visit before its first event.
  if (!payload || payload.type !== "visit") {
    await ensureSessionVisit(sessionId);
  }

  await sendEvent(payload, sessionId);
}

export function trackVisit() {
  if (!_enabled) return;
  const sessionId = getSessionId();
  ensureSessionVisit(sessionId);
}

export function resetTune() {
  clearPendingTune();
  _lastTuneChannel = null;
}

export function trackTune(channelId) {
  if (!_enabled || !channelId || channelId === _lastTuneChannel) return;
  if (_pendingTuneChannel === channelId) return;

  clearPendingTune();
  _pendingTuneChannel = channelId;
  _pendingTuneTimer = setTimeout(() => {
    const stableChannel = _pendingTuneChannel;
    _pendingTuneTimer = null;
    _pendingTuneChannel = null;
    if (!_enabled || !stableChannel || stableChannel === _lastTuneChannel) return;
    _lastTuneChannel = stableChannel;
    post({ type: "tune", channel_id: stableChannel });
  }, TUNE_STABLE_MS);
}

export function trackView(channelId, videoId) {
  if (!_enabled || !videoId) return;
  const key = `${channelId || ""}:${videoId}`;
  if (key === _lastViewKey) return;
  _lastViewKey = key;
  post({ type: "view", channel_id: channelId || null, video_id: videoId });
}

export function trackWatch(channelId, videoId, watchSeconds) {
  if (!_enabled || !videoId) return;
  const seconds = Number.isFinite(watchSeconds) ? Math.max(1, Math.min(60, watchSeconds)) : 0;
  if (!seconds) return;
  post({ type: "watch", channel_id: channelId || null, video_id: videoId, watch_seconds: seconds });
}

export function trackHeartbeat() {
  if (!_enabled) return;
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  post({ type: "heartbeat" });
}

export function startBeaconLoop() {
  if (!_enabled) return () => {};
  trackVisit();
  trackHeartbeat();
  const iv = setInterval(trackHeartbeat, 30000);
  const onVisibility = () => {
    if (document.visibilityState === "visible") trackHeartbeat();
  };
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);
  return () => {
    clearInterval(iv);
    clearPendingTune();
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
  };
}
